// Multi-slide release announcement modal. Designed for occasional broadcasts
// when we ship a meaningful set of features. Triggered by the SysMessageGate
// when the backend returns a recognized "whats_new_*" code.

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
  Image as ImageIcon,
  ShieldCheck,
  Gavel,
  Download,
  ArrowRight,
} from "lucide-react";

const RELEASE_LABEL = "Release · May 2026";

// Slide config. Add or remove slides to change the announcement contents.
// `image` is a fully-qualified URL (Cloudflare Images delivery in this app).
const SLIDES = [
  {
    key: "brand-protection",
    eyebrow: "Now available",
    image:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/06781e8b-d5b3-4894-6080-1459cfcaa700/w=800",
    imageMobile:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/1392364d-bc8b-43f2-4c87-24a8663ef700/w=800",
    title: (
      <>
        A new home for{" "}
        <span className="text-sky-300">Brand Protection</span>
      </>
    ),
    description: (
      <>
        Look-alike domain monitoring now lives in its own dedicated{" "}
        <span className="text-sky-200 font-medium">Brand Protection</span>{" "}
        page. Detect, triage, and act on impersonating domains without
        bouncing between event tabs.
      </>
    ),
    bullets: [
      "Dedicated detection table with WHOIS, DNS, SSL, Title, MX, and screenshot context",
      "Two subtabs: All Domains and Takedown Requests",
      "Sticky filters and a fast-triage detail side panel",
      (
        <>
          Available through the{" "}
          <span className="text-sky-200 font-medium">Brand Protection</span>{" "}
          page for Enterprise and Threat Intelligence license holders
        </>
      ),
    ],
  },
  {
    key: "managed-takedowns",
    eyebrow: "New add-on",
    image:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/17863321-0fd8-4b07-924e-1258ee003900/w=800",
    imageMobile:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/ddfa0fee-98b3-4248-61af-d1e1d53da000/w=800",
    title: (
      <>
        <span className="text-sky-300">Managed Takedowns</span>
      </>
    ),
    description: (
      <>
        Hand off the registrar abuse process to our analysts. File takedowns
        on the{" "}
        <span className="text-sky-200 font-medium">look-alikes WhiteIntel detects</span>
        , or submit your own{" "}
        <span className="text-sky-200 font-medium">Ad Hoc</span>{" "}
        requests for domains you've sourced. We file, follow up, and document
        evidence until they go down.
      </>
    ),
    bullets: [
      (
        <>
          Two ways to file: on detected look-alikes from Brand Protection, or
          via <span className="text-sky-200 font-medium">Ad Hoc Takedown</span>{" "}
          for domains you bring yourself
        </>
      ),
      "$1,000 per 10-takedown pack ($100 each)",
      (
        <>
          Available as an{" "}
          <span className="text-sky-200 font-medium">add-on</span>{" "}
          for Enterprise and Threat Intelligence plans
        </>
      ),
      "Credits refunded on rejections from non-ICANN-compliant registrars",
    ],
  },
  {
    key: "full-log-downloads",
    eyebrow: "Threat Intelligence",
    image:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/b4145904-4f3b-4645-1fe2-cfe970c5f800/w=800",
    imageMobile:
      "https://imagedelivery.net/d5bS5FFwbZOwgmouc9uS9g/af62dd47-24a4-4414-2b57-c9a26bf27200/w=800",
    title: (
      <>
        Go beyond credentials with{" "}
        <span className="text-sky-300">Full Log Downloads</span>
      </>
    ),
    description: (
      <>
        Pull the raw stealer archive for a compromised host — wallets,
        sensitive files, browser state, and host-level artifacts that change
        how fast your team can investigate, scope blast radius, and contain.
        One click from the{" "}
        <span className="text-sky-200 font-medium">History tab</span>.
      </>
    ),
    bullets: [
      (
        <>
          <span className="text-sky-200 font-medium">Crypto wallets, browser state, application data, and host-level artifacts</span>{" "}
          — the raw evidence behind a compromise
        </>
      ),
      "Build forensic-grade evidence packages for executive compromise reviews, lateral movement scoping, and breach disclosure",
      "Per-snapshot download with built-in sandbox-handling warning",
      "Deduplication-aware so you don't pull the same archive twice",
      (
        <>
          Available on{" "}
          <span className="text-sky-200 font-medium">Threat Intelligence</span>{" "}
          subscriptions{" "}
          <span className="text-neutral-400">(when an archive is available)</span>
        </>
      ),
    ],
  },
  {
    key: "ready-to-deploy",
    eyebrow: "Ready to deploy?",
    // No image — replaced by a 3-card recap grid below the description.
    title: (
      <>
        Put it to work for{" "}
        <span className="text-sky-300">your team</span>
      </>
    ),
    description: (
      <>
        Brand Protection, Managed Takedowns, and Full Log Downloads are
        available on{" "}
        <span className="text-sky-200 font-medium">Enterprise</span> and{" "}
        <span className="text-sky-200 font-medium">Threat Intelligence</span>{" "}
        plans. Pick the tier that fits how your team operates — or talk to us
        if you're not sure.
      </>
    ),
    cards: [
      {
        name: "Brand Protection",
        tag: "Enterprise & Threat Intel",
        description: "Look-alike monitoring with WHOIS, DNS, SSL, MX, and screenshot context.",
        icon: ShieldCheck,
      },
      {
        name: "Managed Takedowns",
        tag: "Add-on",
        description: "Analyst-driven registrar abuse filing in 10-takedown packs.",
        icon: Gavel,
      },
      {
        name: "Full Log Downloads",
        tag: "Threat Intelligence",
        description: "Raw stealer archives — wallets, browser state, host artifacts.",
        icon: Download,
      },
    ],
    primaryCta: { label: "View plans", href: "/upgrades" },
    secondaryCta: { label: "Talk to sales", href: "/contact" },
  },
];

export default function WhatsNewModal({
  open,
  onClose,
  enterMs = 420,
  exitMs = 260,
  easing = "cubic-bezier(0.16, 1, 0.3, 1)",
}) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(open);
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);
  const [imgError, setImgError] = useState({}); // { [slideKey]: true }
  const total = SLIDES.length;
  const slide = SLIDES[step];
  const isFirst = step === 0;
  const isLast = step === total - 1;

  // Mount-time entrance animation (same pattern as FrostedModal — start hidden,
  // double-RAF, then transition to shown so the entrance actually animates).
  useEffect(() => {
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setShow(true)));
    } else {
      setShow(false);
    }
  }, [open]);

  const backdropTiming = useMemo(
    () => ({
      transitionDuration: `${show ? 220 : exitMs}ms`,
      transitionTimingFunction: easing,
    }),
    [show, exitMs, easing]
  );
  const panelTiming = useMemo(
    () => ({
      transitionDuration: `${show ? enterMs : exitMs}ms`,
      transitionTimingFunction: easing,
      transitionDelay: show ? "60ms" : "0ms",
    }),
    [show, enterMs, exitMs, easing]
  );

  const handleTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (!show) setVisible(false);
  };

  // ESC + arrow keys
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight" && !isLast) setStep((s) => s + 1);
      else if (e.key === "ArrowLeft" && !isFirst) setStep((s) => s - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, onClose, isFirst, isLast]);

  // Per-slide content fade so the swap reads as deliberate, not a reflow.
  const [contentShow, setContentShow] = useState(true);
  const prevStepRef = useRef(step);
  useEffect(() => {
    if (prevStepRef.current === step) return;
    prevStepRef.current = step;
    setContentShow(false);
    const t = setTimeout(() => setContentShow(true), 60);
    return () => clearTimeout(t);
  }, [step]);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="What's new"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/75 backdrop-blur-sm transition-opacity ${
          show ? "opacity-100" : "opacity-0"
        }`}
        style={backdropTiming}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        onTransitionEnd={handleTransitionEnd}
        className={`relative z-10 w-full max-w-3xl overflow-hidden rounded-md border border-sky-500/20
                    bg-[#0a1224]
                    shadow-[0_30px_70px_-20px_rgba(0,0,0,0.85)]
                    ring-1 ring-white/[0.03]
                    transition-all will-change-transform will-change-opacity
                    ${
                      show
                        ? "opacity-100 translate-y-0 scale-100"
                        : "opacity-0 translate-y-6 scale-[0.97]"
                    }`}
        style={panelTiming}
      >
        {/* Top accent stripe */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sky-400/60 to-transparent z-20" />

        {/* Close — generous hit area, sits above the body via z-30 */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-30 rounded-md p-2 text-neutral-400 hover:text-white hover:bg-white/[0.08] transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Body — text leads. */}
        <div
          className={`relative px-8 pt-7 transition-all duration-300 ${
            contentShow ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
          }`}
        >
          {/* Right padding reserves space for the absolute-positioned close
              button so the "step / total" counter doesn't get tucked under it. */}
          <div className="flex items-center justify-between gap-3 mb-3 pr-12">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold min-w-0">
              <Sparkles className="h-3 w-3 text-sky-300/80 shrink-0" />
              <span className="truncate">{RELEASE_LABEL}</span>
              <span className="text-neutral-600 shrink-0">·</span>
              <span className="text-neutral-400 normal-case tracking-normal font-normal truncate">
                {slide.eyebrow}
              </span>
            </div>
            <div className="text-[11px] text-neutral-500 font-medium tabular-nums shrink-0">
              {step + 1} / {total}
            </div>
          </div>

          <h2 className="text-2xl font-semibold text-white leading-tight tracking-tight">
            {slide.title}
          </h2>

          <p className="mt-3 text-[14px] leading-relaxed text-neutral-300/95">
            {slide.description}
          </p>

          {slide.bullets && slide.bullets.length > 0 && (
            <ul className="mt-4 space-y-2 text-[13px] text-neutral-300">
              {slide.bullets.map((b, i) => (
                <li key={i} className="flex gap-2.5">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sky-400" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Visual area below the text. Slides ship either an image OR a card
            grid (the recap slide uses cards). Card grid is the more enterprise
            way to summarize multiple features without needing a separate
            graphic. */}
        {slide.cards && slide.cards.length > 0 ? (
          <div
            className={`mx-8 mt-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3 transition-opacity duration-300 ${
              contentShow ? "opacity-100" : "opacity-0"
            }`}
          >
            {slide.cards.map((card, i) => {
              const CardIcon = card.icon;
              return (
                <div
                  key={i}
                  className="rounded-lg border border-white/[0.08] bg-gradient-to-br from-sky-500/[0.04] via-[#0e1830] to-[#0a1224] p-4 hover:border-sky-500/30 transition-colors"
                >
                  {CardIcon && (
                    <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-md border border-sky-500/30 bg-sky-500/10">
                      <CardIcon className="h-4 w-4 text-sky-300" />
                    </div>
                  )}
                  <div className="text-sm font-semibold text-white leading-tight">
                    {card.name}
                  </div>
                  <div className="mt-1 text-[9px] uppercase tracking-[0.18em] text-sky-300/70 font-semibold">
                    {card.tag}
                  </div>
                  {card.description && (
                    <p className="mt-2 text-[12px] leading-snug text-neutral-400">
                      {card.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div
            className={`mx-8 mt-6 mb-6 rounded-lg border border-white/[0.06] bg-gradient-to-br from-sky-500/[0.04] via-[#0e1830] to-[#0a1224] overflow-hidden transition-opacity duration-300 ${
              contentShow ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="relative h-64 md:h-72">
              {!imgError[slide.key] ? (
                <picture>
                  {/* Mobile-optimized asset under md (768px). Falls back to the
                      desktop URL when the slide hasn't shipped a mobile version
                      yet, so partial coverage doesn't break the layout. */}
                  <source
                    media="(max-width: 767px)"
                    srcSet={slide.imageMobile || slide.image}
                  />
                  <img
                    src={slide.image}
                    alt=""
                    loading={step === 0 ? "eager" : "lazy"}
                    onError={() =>
                      setImgError((prev) => ({ ...prev, [slide.key]: true }))
                    }
                    className="h-full w-full object-contain"
                  />
                </picture>
              ) : (
                <div className="flex h-full w-full items-center justify-center text-neutral-600">
                  <ImageIcon className="h-8 w-8" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer / nav */}
        <div className="border-t border-white/[0.06] bg-black/20 px-8 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={isFirst}
            className="inline-flex items-center gap-1 rounded-md border border-sky-900/40 px-3 py-1.5 text-sm font-medium text-neutral-300 hover:text-white hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>

          <div className="flex items-center gap-1.5">
            {SLIDES.map((s, i) => {
              const active = i === step;
              return (
                <button
                  key={s.key}
                  onClick={() => setStep(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    active
                      ? "w-8 bg-sky-400"
                      : "w-1.5 bg-neutral-600 hover:bg-neutral-400"
                  }`}
                />
              );
            })}
          </div>

          {!isLast ? (
            <button
              onClick={() => setStep((s) => Math.min(total - 1, s + 1))}
              className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/15 px-3.5 py-1.5 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/60 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : slide.primaryCta ? (
            // Recap slide: explicit Close + secondary "Talk to sales" +
            // primary "View plans". Close gives users a way out without
            // navigating, so the modal doesn't feel like a forced funnel.
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md px-3 py-1.5 text-sm font-medium text-neutral-400 hover:text-white hover:bg-white/[0.04] transition-colors"
              >
                Close
              </button>
              {slide.secondaryCta && (
                <button
                  type="button"
                  onClick={() => {
                    onClose?.();
                    navigate(slide.secondaryCta.href);
                  }}
                  className="rounded-md border border-sky-900/40 px-3 py-1.5 text-sm font-medium text-neutral-300 hover:text-white hover:bg-white/[0.04] hover:border-sky-900/60 transition-colors"
                >
                  {slide.secondaryCta.label}
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  navigate(slide.primaryCta.href);
                }}
                className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/15 px-3.5 py-1.5 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/60 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
              >
                {slide.primaryCta.label}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={onClose}
              className="inline-flex items-center gap-1 rounded-md border border-sky-500/40 bg-sky-500/15 px-3.5 py-1.5 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 hover:border-sky-500/60 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
            >
              Get Started
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
